import { createFileRoute, Link } from '@tanstack/react-router'
import {
  BookOpen,
  Circle,
  CircleCheck,
  CircleHelp,
  MessageCircle,
  Mic,
  Upload,
  WifiOff,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/seller/guide')({
  component: SellerGuidePage,
})

// SellerGuidePage — короткая инструкция для продавцов пилота (T-075).
// Ссылка ведёт из /seller/dashboard. Тексты ревьюит тренер перед запуском:
// формулировки рассчитаны на продавцов без технической подготовки.
function SellerGuidePage() {
  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        to="/seller/dashboard"
        className="-ml-3 inline-flex w-fit items-center gap-1 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        ← На главную
      </Link>

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Как пользоваться</h1>
        <p className="text-sm text-muted-foreground">
          Короткая инструкция на 5 минут: что делать продавцу на пилоте Citilux Coach.
        </p>
      </header>

      <Step
        step={1}
        icon={<Mic className="h-5 w-5" aria-hidden="true" />}
        title="Запишите консультацию на телефон"
        description={
          <>
            <p>Откройте на телефоне приложение «Диктофон» или «Запись голоса».</p>
            <ul className="ml-4 list-disc text-sm text-muted-foreground">
              <li>iPhone: зелёный значок «Диктофон» на главном экране.</li>
              <li>Android: приложение «Запись» или «Диктофон».</li>
            </ul>
            <p>Нажмите «Запись» перед началом консультации, «Стоп» — после.</p>
            <p className="text-sm text-muted-foreground">
              Длительность от 30 секунд до 90 минут. Файлы до 200 МБ. Поддерживаются mp3, m4a, wav.
            </p>
          </>
        }
      />

      <Step
        step={2}
        icon={<Upload className="h-5 w-5" aria-hidden="true" />}
        title="Загрузите запись в ЛК"
        description={
          <>
            <p>
              На главной странице «Кабинет продавца» нажмите круглую кнопку с иконкой загрузки в
              правом нижнем углу.
            </p>
            <ol className="ml-4 list-decimal text-sm text-muted-foreground">
              <li>
                Выберите «Файл консультации» — телефон откроет галерею или список записей диктофона.
              </li>
              <li>Укажите дату консультации (по умолчанию — сегодня).</li>
              <li>По желанию — добавьте короткий комментарий для тренера.</li>
              <li>Нажмите «Загрузить запись».</li>
            </ol>
            <p>Пока идёт загрузка — не закрывайте окно. После загрузки вы вернётесь на главную.</p>
          </>
        }
      />

      <Step
        step={3}
        icon={<CircleCheck className="h-5 w-5" aria-hidden="true" />}
        title="Дождитесь обработки"
        description={
          <>
            <p>Запись пройдёт три этапа — в списке на главной виден статус:</p>
            <ul className="ml-4 text-sm text-muted-foreground">
              <li>
                <span className="font-medium">Загружена</span> — файл получен.
              </li>
              <li>
                <span className="font-medium">Расшифровка</span> — система переводит голос в текст.
              </li>
              <li>
                <span className="font-medium">Анализ</span> — разбор по чек-листу.
              </li>
              <li>
                <span className="font-medium">Готова</span> — можно открывать карточку.
              </li>
            </ul>
            <p>Обычно это занимает 3–10 минут в зависимости от длины записи.</p>
          </>
        }
      />

      <Step
        step={4}
        icon={<BookOpen className="h-5 w-5" aria-hidden="true" />}
        title="Посмотрите разбор"
        description={
          <>
            <p>Откройте запись в списке — внутри увидите:</p>
            <ul className="ml-4 text-sm text-muted-foreground">
              <li>
                <span className="font-medium">Теги</span> — какие приёмы продаж встретились. Нажмите
                на тег, чтобы увидеть цитату из разговора.
              </li>
              <li>
                <span className="font-medium">Чек-лист</span> — шесть критериев работы продавца,
                каждый с оценкой 0 / 1 / 2 и обоснованием.
              </li>
              <li>
                <span className="font-medium">Рекомендации</span> — что сделано хорошо, что упущено,
                и что исправить в следующей консультации.
              </li>
            </ul>
            <p>
              Общий балл — на карточке сверху. В разделе{' '}
              <Link
                to="/seller/analytics"
                className="text-primary underline-offset-4 hover:underline"
              >
                «Моя динамика»
              </Link>{' '}
              видно средние за неделю и месяц.
            </p>
          </>
        }
      />

      <Card className="border-amber-200 bg-amber-50 dark:border-amber-700/30 dark:bg-amber-950/20">
        <CardHeader className="flex flex-row items-start gap-3">
          <CircleHelp
            className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400"
            aria-hidden="true"
          />
          <div>
            <CardTitle className="text-base">Если что-то не работает</CardTitle>
            <CardDescription className="mt-1">Частые ситуации и что с ними делать.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <WifiOff className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <p>
              <span className="font-medium">Нет интернета.</span> Загрузите запись позже — файл на
              телефоне никуда не денется. Система покажет красную плашку вверху экрана, когда связь
              пропадёт.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Circle className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <p>
              <span className="font-medium">
                Запись «застряла» в статусе «Расшифровка» дольше 15 минут.
              </span>{' '}
              Сообщите тренеру — админ перезапустит обработку.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <MessageCircle className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <p>
              <span className="font-medium">Вопрос, которого здесь нет.</span> Напишите тренеру — он
              передаст команде пилота.
            </p>
          </div>
        </CardContent>
      </Card>
    </main>
  )
}

interface StepProps {
  step: number
  icon: React.ReactNode
  title: string
  description: React.ReactNode
}

function Step({ step, icon, title, description }: StepProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
          {step}
        </span>
        <div className="flex flex-col">
          <CardTitle className="flex items-center gap-2 text-base">
            {icon}
            {title}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm leading-relaxed">{description}</CardContent>
    </Card>
  )
}
